if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        // Store the current index before closing
        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          if (this.lightbox.pswp) {
            lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
        });

        // Update slider after lightbox is destroyed
        this.lightbox.on('destroy', () => {
          if (this.sliderInstance && this.sliderInstance.slider && lastLightboxIndex !== null) {
            const slider = this.sliderInstance.slider;
            const targetIndex = lastLightboxIndex;
            // currIndex from lightbox is the index in dataSource array
            // which should match the slider index since both are created from the same media items in the same order
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                slider.slideTo(targetIndex, 0);
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, zoomBtn) => {
            if (zoomBtn.dataset.mediaType === 'image') {
              // zoomBtn is the .js-photoswipe--zoom button, find its parent .product__media-item
              const mediaItem = zoomBtn.closest('.product__media-item');
              const visibleItems = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
              const index = mediaItem ? visibleItems.indexOf(mediaItem) : -1;
              this.lightbox.loadAndOpen(index >= 0 ? index : 0);
            }
          },
        });
      }

      refreshImageZoom() {
        if (!this.lightbox) return;

        // Rebuild dataSource from current visible media items
        const newDataSource = [];
        const visibleMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        visibleMedia.forEach((media) => {
          switch (media.dataset.mediaType) {
            case 'model':
              newDataSource.push({
                id: media.dataset.mediaIndex,
                html: `<div class="pswp__item--${media.dataset.mediaType}">${
                  media.querySelector('product-model').outerHTML
                }</div>`,
                mediaType: media.dataset.mediaType,
              });
              break;
            case 'video':
            case 'external_video':
              newDataSource.push({
                id: media.dataset.mediaIndex,
                html: `<div class="pswp__item--${media.dataset.mediaType}">${
                  media.querySelector('video-element').outerHTML
                }</div>`,
                mediaType: media.dataset.mediaType,
              });
              break;
            case 'image':
              newDataSource.push({
                id: media.dataset.mediaIndex,
                src: media.dataset.src,
                width: media.dataset.pswpWidth,
                height: media.dataset.pswpHeight,
                mediaType: media.dataset.mediaType,
              });
              break;
          }
        });

        // Update the lightbox options dataSource
        this.lightbox.options.dataSource = newDataSource;
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        // Reorder gallery: matching variant images first
        const didReorder = this.filterMediaByVariant(variant);

        let featuredMedia = variant.featured_media;
        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image',
          };
        }

        if (!featuredMedia) return;

        if (didReorder) {
          // After reorder, the slider must be destroyed and reinited
          // because Swiper doesn't re-read DOM order
          // slideTo(0) will be done after reinit
        } else if (this.sliderInstance && this.sliderInstance.slider) {
          const slideIndex = featuredMedia.position || 0;
          this.sliderInstance.slider.slideTo(slideIndex - 1, 0, false);
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      /**
       * Filter gallery to show only images matching ALL selected variant option values.
       * 
       * Alt text format: "ProductName - Brand - OptionVal1 - OptionVal2"
       * Parses variant-relevant segments (after "Trust Industrie" marker).
       * Matches ALL selected option values against these segments.
       * 
       * Examples:
       *   Single option  â Ãcru 23        â shows images with "Ãcru 23" segment
       *   Multi options  â Ecru + Droit   â shows images with BOTH "Ecru" AND "Droit"
       *   No matching alt â shows all images (no filtering)
       * 
       * Returns true if filtering was applied.
       */
      filterMediaByVariant(variant) {
        if (!variant) return false;

        // ---- Cache all original items on first call ----
        if (!this._allMediaItems) {
          this._allMediaItems = Array.from(
            this.elements.mediaList.querySelectorAll('.product__media-item')
          );
        }
        if (!this._allThumbItems) {
          const thumbList = this.querySelector('[id^="Slider-Thumbnails"]');
          this._allThumbItems = thumbList
            ? Array.from(thumbList.querySelectorAll('.product__thumbs-item'))
            : [];
        }

        // ---- Always restore ALL items to DOM first (undo previous filter) ----
        if (this._isFiltered) {
          if (this.thumbsInstance && typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider) {
            this.thumbsInstance.slider.destroy(true, true);
            this.thumbsInstance = false;
          }
          if (this.sliderInstance && typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
            this.sliderInstance.slider.destroy(true, true);
            this.sliderInstance = false;
          }

          this._allMediaItems.forEach((media, idx) => {
            media.dataset.mediaIndex = idx;
            this.elements.mediaList.appendChild(media);
          });
          this.elements.mediaItems = Array.from(this._allMediaItems);

          const tl = this.querySelector('[id^="Slider-Thumbnails"]');
          if (tl && this._allThumbItems.length > 0) {
            this._allThumbItems.forEach(t => tl.appendChild(t));
          }

          const gal = this.closest('.product__media-gallery') || this;
          gal.classList.remove('show-all', 'product__media-gallery--limited');
          const w = gal.querySelector('.see-more-photos-wrapper');
          if (w) w.remove();

          this._isFiltered = false;
        }

        // ---- Helper: extract variant-relevant segments from alt text ----
        // Alt format: "ProductName - Trust Industrie - Color - Angle"
        // We skip everything before and including the brand marker.
        const getVariantSegments = (alt) => {
          if (!alt) return [];
          const segments = alt.split(/\s*-\s*/).map(s => s.trim().toLowerCase()).filter(s => s.length > 0);
          const brandIdx = segments.findIndex(s => s.includes('trust industrie'));
          if (brandIdx >= 0) return segments.slice(brandIdx + 1);
          // Fallback: skip first 2 segments (product name + brand)
          return segments.length > 2 ? segments.slice(2) : segments;
        };

        // ---- Helper: check if a segment matches a value ----
        // Handles exact match AND partial (e.g., "Angle droit" contains "Droit")
        const segmentMatchesValue = (segment, value) => {
          if (segment === value) return true;
          if (segment.includes(value)) return true;
          if (value.includes(segment)) return true;
          return false;
        };

        // ---- Helper: check if alt text variant segments contain a value ----
        const altHasValue = (alt, value) => {
          if (!alt || !value) return false;
          const segments = getVariantSegments(alt);
          const v = value.toLowerCase().trim();
          return segments.some(s => segmentMatchesValue(s, v));
        };

        // ---- Check if any media has alt text ----
        const hasAnyAlt = this._allMediaItems.some(m => (m.dataset.mediaAlt || '').trim().length > 0);
        if (!hasAnyAlt) return false;

        // ---- Find ALL variant option values that match at least one image ----
        // This is the KEY fix: we collect ALL matching options, not just the first
        const activeFilters = [];
        for (let i = 1; i <= 3; i++) {
          const optVal = variant['option' + i];
          if (!optVal) continue;
          if (this._allMediaItems.some(m => altHasValue(m.dataset.mediaAlt, optVal))) {
            activeFilters.push(optVal);
          }
        }

        if (activeFilters.length === 0) return false;

        // ---- Filter: images must match ALL active filters ----
        const matching = [];
        const nonMatching = [];

        this._allMediaItems.forEach((media) => {
          const alt = (media.dataset.mediaAlt || '').trim();
          const mediaType = media.dataset.mediaType || 'image';

          // Non-image media (video, model) always shown
          if (mediaType !== 'image') {
            matching.push(media);
            return;
          }

          // Images without any alt text: show them (generic/shared images)
          if (!alt) {
            matching.push(media);
            return;
          }

          // Check if ALL active filters match this image's alt text
          const allMatch = activeFilters.every(filterVal => altHasValue(alt, filterVal));
          if (allMatch) {
            matching.push(media);
          } else {
            nonMatching.push(media);
          }
        });

        // If strict filtering yields 0 results, try filtering by first filter only (graceful degradation)
        if (matching.length === 0 && activeFilters.length > 1) {
          const primaryFilter = activeFilters[0];
          this._allMediaItems.forEach((media) => {
            const alt = (media.dataset.mediaAlt || '').trim();
            if (!alt || altHasValue(alt, primaryFilter)) {
              matching.push(media);
            } else {
              nonMatching.push(media);
            }
          });
        }

        if (matching.length === 0) return false;

        // ---- Apply filter: destroy Swiper, update DOM, reinit ----
        if (this.thumbsInstance && typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider) {
          this.thumbsInstance.slider.destroy(true, true);
          this.thumbsInstance = false;
        }
        if (this.sliderInstance && typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }

        // Remove non-matching from DOM
        nonMatching.forEach(media => {
          if (media.parentNode) media.parentNode.removeChild(media);
        });

        // Ensure matching items are in DOM in order
        matching.forEach((media, idx) => {
          media.dataset.mediaIndex = idx;
          this.elements.mediaList.appendChild(media);
        });
        this.elements.mediaItems = matching;

        // Filter thumbnails
        const thumbList = this.querySelector('[id^="Slider-Thumbnails"]');
        if (thumbList && this._allThumbItems.length > 0) {
          const matchingIds = new Set(matching.map(m => m.dataset.mediaId));
          this._allThumbItems.forEach(t => {
            if (matchingIds.has(t.dataset.target)) {
              thumbList.appendChild(t);
            } else {
              if (t.parentNode) t.parentNode.removeChild(t);
            }
          });
        }

        // Reset see-more-photos
        const gallery = this.closest('.product__media-gallery') || this;
        gallery.classList.remove('show-all', 'product__media-gallery--limited');
        const oldWrapper = gallery.querySelector('.see-more-photos-wrapper');
        if (oldWrapper) oldWrapper.remove();

        this._isFiltered = true;

        // Reinitialize Swiper and refresh lightbox
        requestAnimationFrame(() => {
          this.updateMediaLayout();
          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideTo(0, 0, false);
          }
          if (typeof window.initSeeMorePhotos === 'function') {
            window.initSeeMorePhotos();
          }
          // Rebuild PhotoSwipe dataSource to match filtered images
          this.refreshImageZoom();
        });

        return true;
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}

